import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Alert, App, Button, Segmented, Select, Space, Tag, Tooltip, Typography, message as staticMessage, theme } from 'antd';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
  PlusCircleFilled,
  QuestionCircleFilled,
  StopFilled,
  WarningFilled,
} from '@ant-design/icons';
import { errorMessage, space } from '@viq-admin/ui';
import type { GridLens, GridLensEmailCell, GridLensRows } from '../BatchTable';
import type { BatchLead, LeadFieldsPatch } from '../types';
import type { LeadFieldStatus, LeadRowSyncStore } from '../useLeadRowSync';
import type { GridLeadRows } from '../useGridLeadRows';
import type { LeadNoteSyncStore } from '../useLeadNoteSync';
import { validateLeadField } from '../inlineEdit';
import { isCrmEmailShape } from './emailShape';
import { crmSourceLabel } from '../../integrations/crmSourceLabel';
import { failureSummary } from '../../leadRules/labels';
import { LEAD_RULES_COPY } from '../../leadRules/copy';
import { commitInChunks } from './chunkedApi';
import { CRM_COPY } from './copy';
import { CRM_GRID_COPY } from './crmGridCopy';
import {
  CRM_KIND_ORDER,
  MATCH_FILTERS,
  cleanFlags,
  inMatchFilter,
  isSelectedForSend,
  kindLabel,
  previewKind,
  type CrmKindInfo,
  type MatchFilter,
} from './crmGridModel';
import { HelpHint, HelpPopover, HintLine } from './HelpHint';
import { MatchDetailsDrawer } from './MatchDetailsDrawer';
import { OwnerPicker, ownerLabel } from './OwnerPicker';
import { OUTCOME_TAG_COLOR, everyLeadWentThrough, heldReasonText, selectionSummaryText, unreadyReason } from './sendSummary';
import { commitBodyFrom, isReady } from './useCrmQueue';
import type { CrmQueueAction, CrmQueueState } from './useCrmQueue';
import type {
  CommitOutcome,
  CommitResultRow,
  CrmOwner,
  CrmQueueApi,
  LeadEditPatch,
  MergePolicy,
  PersonMatchState,
  PersonPreview,
  PreviewSummary,
} from './types';

/**
 * What a host screen gives `CrmViews` to switch on the unified CRM grid (port
 * plan step 6). `BatchScreen` passes it only when the shell opted in with
 * `unifiedCrmGrid` (the portal's company-admin branch); without it Clean,
 * Match and Send keep their own layouts.
 */
export interface CrmGridHost {
  /** Renders the shared leads grid through a lens (BatchScreen's BatchTable). */
  renderGrid: (lens: GridLens) => ReactNode;
  /**
   * The screen's one load of every active lead at the event: the counts
   * read it (D9) and every lens renders it (`useGridLeadRows`).
   */
  rows: GridLeadRows;
  /** The page footer's action slot, where Send's button goes beside Export CSV. */
  actionSlot?: HTMLElement | null;
  /** The inline-edit sync store, so Clean knows when an email fix has saved. */
  leadSync?: LeadRowSyncStore;
  /** The Notes column's save store: a confirmed note save marks the live read stale. */
  noteSync?: LeadNoteSyncStore;
}

export interface CrmGridProps {
  view: CrmQueueState['view'];
  conferenceId: string;
  api: CrmQueueApi;
  source: string;
  results: PersonMatchState[];
  /** The match check's completed-fetch time: how Clean sees a re-check land. */
  resultsUpdatedAt: number;
  previews: PersonPreview[];
  previewLoaded: boolean;
  /** The live read is on its way (or the check that feeds it is). */
  previewLoading: boolean;
  /** The live read failed with nothing to show. */
  previewFailed: boolean;
  summary?: PreviewSummary;
  owners: CrmOwner[];
  state: CrmQueueState;
  dispatch: Dispatch<CrmQueueAction>;
  /** `PreviewResponse.policy` (the tenant default), `fillBlanksOnly` until known. */
  tenantPolicy: MergePolicy;
  /** `policyOverride ??` the tenant default; undefined until the live read lands. */
  effectivePolicy?: MergePolicy;
  overwriteLocked: boolean;
  /** Lead rows the company's lead rules hold back (D9: read off the lead rows); null until loaded. */
  heldCount: number | null;
  /** The Details drawer's in-place edit (unchanged from legacy Match). */
  onEditLead: (personId: string, patch: LeadEditPatch) => Promise<void>;
  /**
   * A Clean email fix saved: re-run the match check, and only that (legacy
   * Clean's `onRowsChanged`). `editEpoch`: the inline-edit store's epoch
   * the fix confirmed at, when it saved through the store.
   */
  onEmailFixSaved: (editEpoch?: number) => void;
  /** The lead drawer closed on a CRM tab. */
  onLeadDetailClosed: () => void;
  /**
   * The live read on screen predates a change to the leads (an edit, a
   * move, a bulk run) and isn't being re-run: Match and Send say so, with
   * Refresh (`onRefresh`), instead of fetching on their own.
   */
  previewStale?: boolean;
  /** The manual re-check (the toolbar's Refresh). */
  onRefresh?: () => void;
  /** A send landed: forced re-check of both queries (legacy Send's `onCommitted`). */
  onCommitted: () => void;
  host: CrmGridHost;
  /** The match check failed with nothing to show; `CrmViews` says so instead. */
  hidden?: boolean;
}

const STATUS_WIDTH = 230;

/**
 * A column title with its "?" beside it. The trigger sits in a sortable,
 * filterable header, so its clicks and keys stop here instead of also
 * toggling the column's sort.
 */
function TitleWithHelp({ title, help }: { title: string; help: ReactNode }) {
  return (
    <Space size={space.xs} align="center">
      <span>{title}</span>
      <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        {help}
      </span>
    </Space>
  );
}

const KIND_LOOK: Record<CrmKindInfo['kind'], { color: string; icon: ReactNode }> = {
  checking: { color: 'default', icon: <LoadingOutlined /> },
  unavailable: { color: 'default', icon: <WarningFilled /> },
  unchecked: { color: 'default', icon: <QuestionCircleFilled /> },
  no_email: { color: 'warning', icon: <WarningFilled /> },
  held: { color: 'error', icon: <StopFilled /> },
  ambiguous: { color: 'gold', icon: <ExclamationCircleFilled /> },
  fields_differ: { color: 'gold', icon: <ExclamationCircleFilled /> },
  in_crm: { color: 'success', icon: <CheckCircleFilled /> },
  new: { color: 'processing', icon: <PlusCircleFilled /> },
  sent_unchanged: { color: 'default', icon: <CheckCircleFilled /> },
};

/**
 * One status, as a Tag. When it opens something (the Details drawer), it is a
 * real button whose name carries the person, so a column of "Already in CRM"
 * buttons stays distinguishable.
 */
function Pill({
  kind,
  label,
  personName,
  onClick,
  testId,
}: {
  kind: CrmKindInfo['kind'];
  label: string;
  personName: string;
  onClick?: () => void;
  testId: string;
}) {
  const look = KIND_LOOK[kind];
  const tag = (
    <Tag color={look.color} icon={look.icon} style={{ margin: 0, fontWeight: 500 }} data-testid={onClick ? undefined : testId}>
      {label}
    </Tag>
  );
  if (!onClick) return tag;
  return (
    <Button
      type="link"
      size="small"
      data-testid={testId}
      aria-haspopup="dialog"
      aria-label={`${label}, ${personName}`}
      onClick={onClick}
      style={{ padding: 0, height: 'auto' }}
    >
      {tag}
    </Button>
  );
}

/** The status cell's stack: pills first, then the small lines under them. */
function Cell({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start', minWidth: 0 }}>
      {children}
    </div>
  );
}

function Caption({
  children,
  type = 'secondary',
  testId,
}: {
  children: ReactNode;
  type?: 'secondary' | 'warning' | 'danger';
  testId?: string;
}) {
  return (
    <Typography.Text type={type} style={{ fontSize: 12, whiteSpace: 'normal' }} data-testid={testId}>
      {children}
    </Typography.Text>
  );
}

/**
 * Where a Clean email fix stands: saving, saved (waiting on the re-check),
 * bounced, or — a fix saved through the CRM API — failed.
 */
interface EmailFix {
  phase: 'saving' | 'saved' | 'bounced' | 'failed';
  /**
   * How it saves: the grid's inline editing (the sync store), or, with that
   * off, the CRM API's lead edit (`updateLeadFields`), as legacy Clean does.
   */
  via: 'inline' | 'crm';
  /** The match check's completed-fetch time when the save confirmed. */
  since: number;
  name: string;
  /** A CRM-API fix's email while it is saving or failed. */
  value?: string;
}

const noSubscribe = () => () => {};
const noVersion = () => 0;
const noop = () => {};
const EMPTY_LEADS: BatchLead[] = [];
/** A row's name in accessible names when the lead has none (BatchTable's own fallback). */
const NO_NAME = '(no name)';
const IDLE_STATUS: LeadFieldStatus = { kind: 'idle' };
const SAVING_STATUS: LeadFieldStatus = { kind: 'saving', waiting: null };

/**
 * Clean's email rule, on both of its saves: the CRM engine's shape (legacy
 * Clean's check, `emailShape.ts`) — never the looser lead-edit pattern, so
 * Clean never saves an address the engine then rejects — within the lead
 * edit's length cap. A blank is not an address: Clean never clears one.
 */
function cleanEmailRule(value: string): string | null {
  return isCrmEmailShape(value) ? validateLeadField('email', value) : CRM_COPY.emailShapeInvalid;
}

/**
 * The unified CRM grid (port plan step 6): Clean, Match and Send rendered as
 * lenses on the Leads tab's own grid. A tab changes which rows show, what the
 * "In your CRM" column says and what can be done to them, never the layout.
 *
 * Every rule that decides who is SENT is the legacy one: Send's ticks are
 * `isSelectedForSend` (= `commitBodyFrom`'s filter) over the queue's own
 * `deselected` state, and the commit body is `commitBodyFrom`, unchanged.
 * Ambiguous and rule-held people stay un-tickable exactly as today, with no
 * "pick the record" and no "send anyway" (D3). Owner issues are reported,
 * never a block. The live read stays lazy (D8): before Match or Send is
 * opened, nothing here asks for it.
 *
 * Mounted for the life of `CrmViews` (Leads tab included, where it draws no
 * grid), so a send's outcomes and Match's filter survive a tab switch the way
 * the legacy views' mounted-not-unmounted panels did.
 */
export function CrmGrid({
  view,
  conferenceId,
  api,
  source,
  results,
  resultsUpdatedAt,
  previews,
  previewLoaded,
  previewLoading,
  previewFailed,
  summary,
  owners,
  state,
  dispatch,
  tenantPolicy,
  effectivePolicy,
  overwriteLocked,
  heldCount,
  onEditLead,
  onEmailFixSaved,
  onLeadDetailClosed,
  onCommitted,
  host,
  hidden,
  previewStale,
  onRefresh,
}: CrmGridProps) {
  const { token } = theme.useToken();
  // antd's App context when the shell mounts one (both do), so a toast takes
  // the shell's theme; the static API otherwise, as the legacy views use.
  const app = App.useApp();
  // (Typed as always present; without an <App> it is an empty object.)
  const message = typeof app.message?.success === 'function' ? app.message : staticMessage;
  const crm = crmSourceLabel(source);
  const canManage = api.canManage;

  const previewById = useMemo(() => new Map(previews.map((p) => [p.personId, p])), [previews]);
  const matchById = useMemo(() => new Map(results.map((r) => [r.personId, r])), [results]);

  const kindOf = (personId: string): CrmKindInfo =>
    previewKind({
      preview: previewById.get(personId),
      match: matchById.get(personId),
      previewLoaded,
      previewFailed,
      previewScope: state.previewScope,
      choices: state.fieldChoices[personId],
      overwriteLocked,
    });

  // ── Send's results ───────────────────────────────────────────────────────
  const [outcome, setOutcome] = useState<CommitOutcome | null>(null);
  const outcomeById = useMemo(
    () => new Map((outcome?.results ?? []).map((r) => [r.personId, r])),
    [outcome],
  );
  const [busy, setBusy] = useState(false);
  // The rows every lens shows: the screen's one load of the event's active
  // leads. A send and a Details-drawer edit re-read them (`onCommitted`,
  // `onEditLead`); an edit is written through instead.
  const rowsData = host.rows.data;
  const rowsEpochs = useMemo(
    () => (rowsData ? { field: rowsData.fieldEpoch, note: rowsData.noteEpoch } : null),
    [rowsData],
  );
  const lensRows: GridLensRows = {
    leads: rowsData?.leads ?? EMPTY_LEADS,
    loading: host.rows.loading,
    error: host.rows.failed ? CRM_GRID_COPY.rowsFailed : null,
    onRetry: host.rows.reload,
    epochs: rowsEpochs,
  };

  // ── Match ────────────────────────────────────────────────────────────────
  const [detailsId, setDetailsId] = useState<string | null>(null);
  // The Details drawer portals to document.body: close it off Match.
  useEffect(() => {
    if (view !== 'match') setDetailsId(null);
  }, [view]);
  const [ownerChecked, setOwnerChecked] = useState<Set<string>>(new Set());
  // Match opens on "Needs your decision" when there is anything to decide,
  // decided once, the first time Match is shown with the live read in; the
  // operator's own choice then stands.
  const [matchFilter, setMatchFilter] = useState<MatchFilter | null>(null);

  const matchInfos = useMemo(
    () =>
      previews.map((p) => ({
        p,
        info: previewKind({
          preview: p,
          match: matchById.get(p.personId),
          previewLoaded,
          previewFailed,
          previewScope: state.previewScope,
          choices: state.fieldChoices[p.personId],
          overwriteLocked,
        }),
      })),
    [previews, matchById, previewLoaded, previewFailed, state.previewScope, state.fieldChoices, overwriteLocked],
  );
  const filterCounts = useMemo(() => {
    const counts = {} as Record<MatchFilter, number>;
    for (const f of MATCH_FILTERS) {
      counts[f] = matchInfos.filter(({ p, info }) => inMatchFilter(f, p, info, matchById.get(p.personId)?.bucket)).length;
    }
    return counts;
  }, [matchInfos, matchById]);
  if (matchFilter === null && previewLoaded && view === 'match') {
    setMatchFilter(filterCounts.decide > 0 ? 'decide' : 'all');
  }
  const shownMatchFilter: MatchFilter = matchFilter ?? 'all';

  // ── Clean: telling the operator a fixed lead moved on, or that it didn't ─
  const [emailFixes, setEmailFixes] = useState<Map<string, EmailFix>>(new Map());
  const leadSync = host.leadSync;
  const syncVersion = useSyncExternalStore(leadSync?.subscribe ?? noSubscribe, leadSync?.getVersion ?? noVersion);
  const onEmailFixSavedRef = useRef(onEmailFixSaved);
  onEmailFixSavedRef.current = onEmailFixSaved;
  const emailFixesRef = useRef(emailFixes);
  emailFixesRef.current = emailFixes;
  const resultsUpdatedAtRef = useRef(resultsUpdatedAt);
  resultsUpdatedAtRef.current = resultsUpdatedAt;
  // Each fix's endings happen once: these effects can run again from a render
  // that predates their own state update (a match re-check landing first),
  // so they move only the very fix object they saw, through functional
  // updates, and act on it once.
  const settledRef = useRef(new WeakSet<EmailFix>());
  const toastedRef = useRef(new WeakSet<EmailFix>());
  // A saving inline fix confirms (or fails) in the sync store. On confirm,
  // re-run the match check at once, as legacy Clean did after its own save.
  useEffect(() => {
    if (!leadSync) return;
    const settled = new Map<string, { fix: EmailFix; failed: boolean }>();
    for (const [id, fix] of emailFixes) {
      if (fix.phase !== 'saving' || fix.via !== 'inline' || settledRef.current.has(fix)) continue;
      const status = leadSync.status(id, 'email');
      if (status.kind === 'saving') continue;
      settledRef.current.add(fix);
      // A failure: the cell shows it and its Retry; nothing to wait for.
      settled.set(id, { fix, failed: status.kind === 'failed' });
    }
    if (settled.size === 0) return;
    const since = resultsUpdatedAt;
    setEmailFixes((prev) => {
      let next: Map<string, EmailFix> | null = null;
      for (const [id, { fix, failed }] of settled) {
        if (prev.get(id) !== fix) continue;
        next ??= new Map(prev);
        if (failed) next.delete(id);
        else next.set(id, { ...fix, phase: 'saved', since });
      }
      return next ?? prev;
    });
    // The epoch tells the edit burst this fix is part of that it is covered.
    if ([...settled.values()].some((s) => !s.failed)) onEmailFixSavedRef.current(leadSync.epoch());
  }, [syncVersion, leadSync, resultsUpdatedAt, emailFixes]);
  // A re-check that completed after the save either moved the lead on (the
  // CRM can be checked with the new email) or bounced it (saved, but still no
  // usable email) — legacy Clean's two endings. A CRM-API fix that failed is
  // dropped, quietly, once the lead no longer needs an email (it got one
  // another way): there is nothing left for its Retry to do.
  useEffect(() => {
    const ended = new Map<string, { fix: EmailFix; moved: boolean; quiet?: boolean }>();
    for (const [id, fix] of emailFixes) {
      const needsEmail = matchById.get(id)?.bucket === 'needs_email';
      if (fix.phase === 'failed') {
        if (!needsEmail) ended.set(id, { fix, moved: true, quiet: true });
        continue;
      }
      if (fix.phase !== 'saved' && fix.phase !== 'bounced') continue;
      if (!needsEmail) ended.set(id, { fix, moved: true });
      else if (fix.phase === 'saved' && resultsUpdatedAt > fix.since) ended.set(id, { fix, moved: false });
    }
    if (ended.size === 0) return;
    setEmailFixes((prev) => {
      let next: Map<string, EmailFix> | null = null;
      for (const [id, { fix, moved }] of ended) {
        if (prev.get(id) !== fix) continue;
        next ??= new Map(prev);
        if (moved) next.delete(id);
        else next.set(id, { ...fix, phase: 'bounced' });
      }
      return next ?? prev;
    });
    for (const { fix, moved, quiet } of ended.values()) {
      if (!moved || quiet || toastedRef.current.has(fix)) continue;
      toastedRef.current.add(fix);
      void message.success(CRM_GRID_COPY.clean.movedOn(fix.name));
    }
  }, [matchById, resultsUpdatedAt, emailFixes, message]);

  function onCleanEdit(lead: BatchLead, patch: LeadFieldsPatch) {
    if (patch.email === undefined) return;
    setEmailFixes((prev) => {
      const next = new Map(prev);
      if (patch.email?.trim() && matchById.get(lead.id)?.bucket === 'needs_email') {
        next.set(lead.id, { phase: 'saving', via: 'inline', since: resultsUpdatedAt, name: lead.preview || 'This lead' });
      } else {
        next.delete(lead.id);
      }
      return next;
    });
  }

  /**
   * Clean's email fix with the grid's inline editing off (no `leadFieldsApi`,
   * or a backend without its route): the CRM API's lead edit, exactly as
   * legacy Clean saves (`updateLeadFields`: read, then PATCH), one at a time
   * per lead. Then the row shows the email (written through to the shared
   * rows) and the match check alone re-runs, as legacy Clean's
   * `onRowsChanged` — with one re-read of the shared rows beside it: this
   * save's answer carries no `completeness`, so only a re-read can tell
   * whether the lead is still held (by a missing email, say). This path
   * only runs against a backend without viq-backend#117.
   */
  async function saveEmailViaCrm(lead: BatchLead, email: string) {
    if (!canManage || emailFixesRef.current.get(lead.id)?.phase === 'saving') return;
    const name = lead.preview || 'This lead';
    const put = (fix: EmailFix) => setEmailFixes((prev) => new Map(prev).set(lead.id, fix));
    put({ phase: 'saving', via: 'crm', since: resultsUpdatedAtRef.current, name, value: email });
    try {
      await api.updateLeadFields(lead.id, { email });
    } catch {
      put({ phase: 'failed', via: 'crm', since: resultsUpdatedAtRef.current, name, value: email });
      void message.error(CRM_COPY.saveEmailFailed);
      return;
    }
    host.rows.patchRow(lead.id, (row) => ({ ...row, email }));
    put({ phase: 'saved', via: 'crm', since: resultsUpdatedAtRef.current, name });
    onEmailFixSavedRef.current();
    host.rows.reload();
  }

  function dropEmailFix(leadId: string) {
    setEmailFixes((prev) => {
      if (!prev.has(leadId)) return prev;
      const next = new Map(prev);
      next.delete(leadId);
      return next;
    });
  }

  /** The Email cell's own editor on Clean when inline editing is off (see `saveEmailViaCrm`). */
  function cleanEmailCell(lead: BatchLead): GridLensEmailCell | null {
    if (!canManage) return null;
    const fix = emailFixes.get(lead.id);
    if (fix?.via === 'crm' && fix.phase === 'saving') {
      return { value: fix.value, status: SAVING_STATUS, locked: true, onSave: noop, onRetry: noop, onDiscard: noop };
    }
    if (fix?.via === 'crm' && fix.phase === 'failed') {
      return {
        value: fix.value,
        status: { kind: 'failed', message: CRM_COPY.saveEmailFailed },
        onSave: (email) => void saveEmailViaCrm(lead, email),
        onRetry: () => void saveEmailViaCrm(lead, fix.value ?? ''),
        onDiscard: () => dropEmailFix(lead.id),
      };
    }
    // Saved and waiting on its re-check: nothing to do until it lands.
    if (fix?.phase === 'saved') return null;
    // Legacy Clean's queue: the people the check can't run for want of an email.
    if (matchById.get(lead.id)?.bucket !== 'needs_email') return null;
    return {
      value: lead.email,
      status: IDLE_STATUS,
      onSave: (email) => void saveEmailViaCrm(lead, email),
      onRetry: noop,
      onDiscard: noop,
    };
  }

  // ── Send ────────────────────────────────────────────────────────────────
  const readyIds = useMemo(() => new Set(previews.filter(isReady).map((p) => p.personId)), [previews]);
  const selectedPreviews = useMemo(
    () => previews.filter((p) => isSelectedForSend(p, state)),
    [previews, state],
  );
  const selectedIds = useMemo(() => selectedPreviews.map((p) => p.personId), [selectedPreviews]);
  const sendPolicy: MergePolicy = state.policyOverride ?? tenantPolicy;
  const { fieldChoices, ownerPicks } = state;
  const summaryText = useMemo(
    () => selectionSummaryText(selectedPreviews, { fieldChoices, ownerPicks }, sendPolicy),
    [selectedPreviews, fieldChoices, ownerPicks, sendPolicy],
  );
  const integrationAccountOwnedCount = previews.filter(
    (p) => p.ownerRequired && p.ownerProvenance === 'integration_account',
  ).length;
  const ownerUnverifiedCount = previews.filter((p) => p.ownerRequired && p.ownerUnverified === true).length;
  const needsEmailCount = summary?.needsEmail ?? 0;
  const ownersUnavailable = summary?.ownersUnavailable === true;
  const collapsedCount = state.previewScope === 'changed' ? (summary?.collapsed ?? 0) : 0;
  // The grid shows whom Send commits only once the event's leads are in:
  // never armed while they load or after they failed (a 429, say).
  const rowsReason = host.rows.failed
    ? CRM_GRID_COPY.send.rowsFailed
    : host.rows.loading
      ? CRM_GRID_COPY.send.rowsLoading
      : null;
  const sendDisabledReason = !canManage
    ? CRM_COPY.help.sendDisabledReadOnly.text
    : rowsReason
      ? rowsReason
      : previewFailed
      ? CRM_COPY.previewFailedLine
      : previewLoading
        ? CRM_COPY.previewLoadingLine
        : selectedIds.length === 0
          ? CRM_COPY.help.sendDisabledNoSelection.text
          : null;

  async function onSend() {
    if (sendDisabledReason !== null) return;
    setBusy(true);
    try {
      const body = commitBodyFrom({ previews, state });
      // Through the body-cap chunker, never `api.commit` directly.
      const result = await commitInChunks(api, conferenceId, source, body);
      setOutcome(result);
      if (everyLeadWentThrough(result)) {
        void message.success(CRM_COPY.sendSuccessToast);
      } else {
        void message.warning(CRM_COPY.sendPartialFailureToast);
      }
      // Only the rows that actually went out are spent (review fix I2) …
      dispatch({ type: 'resetAfterSend', sentIds: body.persons.map((row) => row.personId) });
      // … and, as legacy Send's "Back to the list" did, they are unticked, so
      // a second press can't re-send them against a stale preview.
      const sentIds = result.results.map((r) => r.personId);
      if (sentIds.length > 0) dispatch({ type: 'setAllSelected', personIds: sentIds, selected: false });
      // Re-reads both queries and the rows (their "Sent" dates move).
      onCommitted();
    } catch (err) {
      // Spec 2026-09-28 §7: a 409 is a stop someone can act on, and its
      // message says what to do — longer than antd's 3 s default.
      const conflict = (err as { statusCode?: number } | null)?.statusCode === 409;
      if (conflict) {
        void message.error({ content: errorMessage(err, CRM_COPY.sendFailedToast), duration: 10 });
      } else {
        void message.error(CRM_COPY.sendFailedToast);
      }
    } finally {
      setBusy(false);
    }
  }

  const sendButton = (
    <Button
      type="primary"
      data-testid="crm-grid-send-button"
      loading={busy}
      disabled={sendDisabledReason !== null || busy}
      onClick={() => void onSend()}
    >
      {CRM_GRID_COPY.send.button(selectedIds.length, crm)}
    </Button>
  );

  // Match / Send: the live read predates a change to the leads.
  const staleLine =
    previewStale && onRefresh ? (
      <Space size={space.xs} align="center" wrap data-testid="crm-grid-preview-stale">
        <WarningFilled style={{ color: token.colorWarning }} aria-hidden />
        <Typography.Text type="warning">{CRM_GRID_COPY.previewStale}</Typography.Text>
        <Typography.Text type="secondary" aria-hidden>
          ·
        </Typography.Text>
        <Button type="link" size="small" onClick={onRefresh} style={{ padding: 0, height: 'auto' }}>
          {CRM_COPY.refreshButton}
        </Button>
      </Space>
    ) : null;

  // ── Shared status-cell parts ─────────────────────────────────────────────
  function sentCaption(lead: BatchLead) {
    return lead.crmSentAt ? (
      <Caption testId={`crm-grid-sent-${lead.id}`}>{CRM_GRID_COPY.sentOn(lead.crmSentAt)}</Caption>
    ) : null;
  }

  function ownerFlag(p: PersonPreview) {
    if (!p.ownerRequired) return null;
    const help =
      p.ownerProvenance === 'integration_account'
        ? p.currentOwner
          ? CRM_COPY.help.matchOwnerIntegrationAccountExisting
          : CRM_COPY.help.matchOwnerIntegrationAccount
        : p.ownerUnverified === true
          ? CRM_COPY.help.matchOwnerUnverified
          : null;
    if (!help) return null;
    return (
      <Space size={space.xs} data-testid={`crm-grid-owner-flag-${p.personId}`}>
        <Caption type="warning">{help.label}</Caption>
        <HelpHint {...help} testId={`crm-grid-help-owner-${p.personId}`} />
      </Space>
    );
  }

  function kindPill(lead: BatchLead, info: CrmKindInfo, onOpen?: () => void, testId?: string) {
    return (
      <Pill
        kind={info.kind}
        label={kindLabel(info)}
        personName={lead.preview || NO_NAME}
        onClick={onOpen}
        testId={testId ?? `crm-grid-status-${lead.id}`}
      />
    );
  }

  /** The pill's "?" for a status whose meaning isn't obvious from its words. */
  function kindHelp(lead: BatchLead, info: CrmKindInfo) {
    const help =
      info.kind === 'ambiguous'
        ? CRM_COPY.help.matchAmbiguous
        : info.kind === 'no_email'
          ? // On Clean an emailless lead is usually still sent (matched by
            // VendoIQ id); on Match / Send this status means the live read
            // agreed it has none, and it isn't part of the send.
            view === 'clean'
            ? CRM_COPY.help.cleanWaiting
            : CRM_GRID_COPY.help.noEmailNotSent
          : info.kind === 'held'
            ? CRM_GRID_COPY.help.held
            : info.kind === 'unchecked'
              ? CRM_GRID_COPY.help.unchecked
              : info.kind === 'unavailable'
                ? CRM_GRID_COPY.help.unavailable
                : info.kind === 'sent_unchanged'
                  ? CRM_GRID_COPY.help.sentUnchanged
                  : info.kind === 'fields_differ'
                    ? CRM_GRID_COPY.help.fieldsDiffer
                    : null;
    return help ? <HelpHint {...help} testId={`crm-grid-help-${info.kind}-${lead.id}`} /> : null;
  }

  function pillRow(lead: BatchLead, info: CrmKindInfo, onOpen?: () => void, testId?: string) {
    return (
      <Space size={space.xs} align="center" wrap>
        {kindPill(lead, info, onOpen, testId)}
        {kindHelp(lead, info)}
      </Space>
    );
  }

  const statusTitle = (
    <TitleWithHelp
      title={CRM_GRID_COPY.statusTitle}
      help={<HelpPopover {...CRM_GRID_COPY.help.statuses} testId="crm-grid-help-statuses" placement="bottom" />}
    />
  );
  const statusSort = (lead: BatchLead) => CRM_KIND_ORDER[kindOf(lead.id).kind];
  const statusFilterValue = (lead: BatchLead) => kindLabel(kindOf(lead.id));

  // ── Clean lens ───────────────────────────────────────────────────────────
  const cleanNoEmail = results.filter((r) => r.bucket === 'needs_email').length;
  const cleanLens: GridLens = {
    key: 'clean',
    rows: lensRows,
    rowFilter: (l) => {
      const f = cleanFlags(l, matchById.get(l.id));
      return f.noEmail || f.held;
    },
    statusColumn: {
      title: statusTitle,
      width: STATUS_WIDTH,
      render: (l) => {
        const f = cleanFlags(l, matchById.get(l.id));
        const fix = emailFixes.get(l.id);
        return (
          <Cell>
            {f.noEmail && pillRow(l, { kind: 'no_email', conflicts: 0 })}
            {/* A lead can wait on Clean for both reasons: one row, two pills. */}
            {f.held &&
              pillRow(l, { kind: 'held', conflicts: 0 }, undefined, f.noEmail ? `crm-grid-status-held-${l.id}` : undefined)}
            {f.held && l.completeness && (
              <Caption type="warning" testId={`crm-grid-held-reason-${l.id}`}>
                {heldReasonText(l.completeness)}
              </Caption>
            )}
            {fix?.phase === 'saved' && (
              <Caption testId={`crm-grid-email-saved-${l.id}`}>{CRM_COPY.savedPendingRefresh}</Caption>
            )}
            {fix?.phase === 'bounced' && (
              <Space size={space.xs} data-testid={`crm-grid-email-bounced-${l.id}`}>
                <Caption type="danger">{CRM_COPY.savedStillNeedsEmail}</Caption>
                <HelpHint {...CRM_COPY.help.cleanSavedStillNeedsEmail} testId={`crm-grid-help-bounced-${l.id}`} />
              </Space>
            )}
          </Cell>
        );
      },
      sortValue: (l) => (cleanFlags(l, matchById.get(l.id)).held ? 0 : 1),
      filterValue: (l) => {
        const f = cleanFlags(l, matchById.get(l.id));
        return [f.noEmail ? CRM_GRID_COPY.kind.no_email : null, f.held ? CRM_GRID_COPY.kind.held : null]
          .filter(Boolean)
          .join(' · ');
      },
    },
    toolbar: <CleanToolbar noEmail={cleanNoEmail} held={heldCount} />,
    emptyText: CRM_GRID_COPY.clean.empty,
    emailEdit: { validate: cleanEmailRule, fallback: cleanEmailCell },
    onLeadEdit: onCleanEdit,
    onLeadDetailClose: onLeadDetailClosed,
  };

  // ── Match lens ───────────────────────────────────────────────────────────
  const assignableIds = useMemo(
    () => new Set(previews.filter((p) => p.bucket !== 'ambiguous' && p.ownerRequired).map((p) => p.personId)),
    [previews],
  );
  const ownerOptions = owners.map((o) => ({ value: o.id, label: ownerLabel(o) }));
  const matchToolbar = (
    <Space direction="vertical" size={space.sm} style={{ width: '100%' }}>
      {staleLine}
      <Space size={space.lg} wrap align="center">
        <Segmented<MatchFilter>
          aria-label={CRM_GRID_COPY.match.filterLabel}
          value={shownMatchFilter}
          onChange={(f) => setMatchFilter(f)}
          options={MATCH_FILTERS.map((f) => ({
            value: f,
            label: (
              <span
                data-testid={`crm-grid-match-filter-${f}`}
                style={
                  f === 'decide' && filterCounts.decide > 0
                    ? { color: token.colorWarningText, fontWeight: 600 }
                    : undefined
                }
              >
                {/* No number before the live read: unknown is not 0 (D8). */}
                {previewLoaded
                  ? `${CRM_GRID_COPY.match.filters[f]} (${filterCounts[f]})`
                  : CRM_GRID_COPY.match.filters[f]}
              </span>
            ),
          }))}
        />
        <Space size={space.xs} align="center" wrap data-testid="crm-merge-policy">
          <Typography.Text type="secondary">{CRM_GRID_COPY.match.policyLabel}</Typography.Text>
          {/* Today's two options, values and enablement (MergePolicyControl):
              inert until the tenant default is known, without manage rights,
              and under the company's overwrite lock (fill blanks shown). */}
          <Select<MergePolicy>
            aria-label={CRM_COPY.policyGroupLabel}
            size="small"
            style={{ width: 340 }}
            popupMatchSelectWidth={false}
            value={overwriteLocked ? 'fillBlanksOnly' : (effectivePolicy ?? tenantPolicy)}
            disabled={!canManage || effectivePolicy === undefined || overwriteLocked}
            onChange={(policy) => dispatch({ type: 'setPolicyOverride', policy })}
            options={[
              { value: 'fillBlanksOnly', label: CRM_COPY.policyFillBlanks, title: CRM_COPY.policyFillBlanks },
              { value: 'overwrite', label: CRM_COPY.policyOverwrite, title: CRM_COPY.policyOverwrite },
            ]}
          />
          <HelpPopover {...CRM_COPY.help.matchPolicy} testId="crm-help-match-policy" />
          {overwriteLocked && <Typography.Text type="secondary">{CRM_COPY.policyLockedNote}</Typography.Text>}
        </Space>
      </Space>
      {collapsedCount > 0 && <CollapsedLine count={collapsedCount} dispatch={dispatch} />}
      {canManage && assignableIds.size > 0 && (
        <Space wrap align="center">
          <Typography.Text strong>{CRM_COPY.setCrmOwnerForSelected}</Typography.Text>
          <HelpHint {...CRM_COPY.help.matchBulkAssign} testId="crm-help-match-bulk-assign" />
          <Select
            aria-label={CRM_COPY.setCrmOwnerForSelected}
            showSearch
            style={{ minWidth: 220 }}
            placeholder={CRM_COPY.ownerPickerPlaceholder}
            disabled={ownerChecked.size === 0}
            options={ownerOptions}
            value={undefined}
            filterOption={(input, option) =>
              (option?.label as string | undefined)?.toLowerCase().includes(input.toLowerCase()) ?? false
            }
            onChange={(ownerId: string) => {
              dispatch({ type: 'bulkAssignOwner', personIds: Array.from(ownerChecked), ownerId });
              setOwnerChecked(new Set());
            }}
          />
        </Space>
      )}
    </Space>
  );

  const matchLens: GridLens = {
    key: 'match',
    rows: lensRows,
    rowFilter: (l) => {
      const p = previewById.get(l.id);
      return !!p && inMatchFilter(shownMatchFilter, p, kindOf(l.id), matchById.get(l.id)?.bucket);
    },
    statusColumn: {
      title: statusTitle,
      width: STATUS_WIDTH,
      render: (l) => {
        const p = previewById.get(l.id);
        const info = kindOf(l.id);
        if (!p) return <Cell>{pillRow(l, info)}</Cell>;
        const opensDetails = p.bucket === 'existing';
        const remoteType =
          p.remoteType === 'lead'
            ? { tag: CRM_COPY.remoteTypeLabel.lead, help: CRM_COPY.help.matchLeadTag }
            : p.remoteType === 'contact'
              ? { tag: CRM_COPY.remoteTypeLabel.contact, help: CRM_COPY.help.matchContactTag }
              : null;
        const activities = p.activitiesToPush;
        return (
          <Cell>
            {pillRow(l, info, opensDetails ? () => setDetailsId(l.id) : undefined)}
            {opensDetails && remoteType && (
              <Space size={space.xs}>
                <Caption>{remoteType.tag}</Caption>
                <HelpHint {...remoteType.help} testId={`crm-grid-help-type-${l.id}`} />
              </Space>
            )}
            {info.kind === 'held' && <Caption type="warning">{unreadyReason(p)}</Caption>}
            {p.bucket === 'ambiguous' && <Caption>{CRM_COPY.ambiguousNote}</Caption>}
            {p.bucket === 'new' && p.similar && (
              <Space size={space.xs}>
                <Caption>{`${CRM_COPY.similarNotePrefix} ${p.similar} ${CRM_COPY.similarNoteSuffix}`}</Caption>
                <HelpHint {...CRM_COPY.help.matchSimilar} testId={`crm-grid-help-similar-${l.id}`} />
              </Space>
            )}
            {/* Never on ambiguous rows: nothing is logged against them. */}
            {p.bucket !== 'ambiguous' && activities && activities.calls + activities.tasks > 0 && (
              <Caption testId={`crm-grid-activities-${l.id}`}>
                {CRM_COPY.activity.willLog(activities.calls, activities.tasks)}
              </Caption>
            )}
            {sentCaption(l)}
          </Cell>
        );
      },
      sortValue: statusSort,
      filterValue: statusFilterValue,
    },
    extraColumns: [
      {
        key: 'crmOwner',
        // Legacy Match stated the owner rule once per column; here, once.
        title: (
          <TitleWithHelp
            title={CRM_GRID_COPY.ownerTitle}
            help={<HelpHint {...CRM_COPY.help.matchOwner} testId="crm-help-match-owner" placement="bottom" />}
          />
        ),
        width: 300,
        render: (l) => {
          const p = previewById.get(l.id);
          // Ambiguous people change nothing in the CRM, owner included.
          if (!p || p.bucket === 'ambiguous') return <Typography.Text type="secondary">—</Typography.Text>;
          return (
            <Space direction="vertical" size={space.xs} style={{ width: '100%', minWidth: 0 }}>
              <OwnerPicker
                owners={owners}
                value={state.ownerPicks[p.personId]}
                suggestedOwnerId={p.suggestedOwnerId}
                currentOwner={p.currentOwner}
                ownerRequired={p.ownerRequired}
                ownerProvenance={p.ownerProvenance}
                ownerUnverified={p.ownerUnverified}
                disabled={!canManage}
                onPick={(ownerId) => dispatch({ type: 'pickOwner', personId: p.personId, ownerId })}
                ariaLabel={`Owner for ${p.displayName}`}
              />
              {p.ownerRequired && p.currentOwner && (
                <Caption testId={`match-current-owner-unusable-${p.personId}`}>
                  {CRM_COPY.currentOwnerUnusableNote(p.currentOwner.name)}
                </Caption>
              )}
            </Space>
          );
        },
      },
    ],
    defaultSort: { columnKey: 'crmStatus', order: 'ascend' },
    // Ticks for the bulk CRM-owner set, as legacy Match's checkboxes; none
    // at all without manage rights.
    selection:
      canManage && assignableIds.size > 0
        ? {
            selectedIds: Array.from(ownerChecked),
            onChange: (ids) => setOwnerChecked(new Set(ids.filter((id) => assignableIds.has(id)))),
            disabledReason: (l) => {
              const p = previewById.get(l.id);
              if (!p) return CRM_GRID_COPY.match.ownerNotYet;
              if (p.bucket === 'ambiguous') return CRM_COPY.ambiguousNote;
              return assignableIds.has(l.id) ? null : CRM_GRID_COPY.match.ownerNotNeeded;
            },
            ariaLabel: (l) => `Choose ${l.preview || NO_NAME}`,
          }
        : undefined,
    toolbar: matchToolbar,
    emptyText: previewLoading
      ? CRM_COPY.previewLoadingLine
      : previewFailed
        ? CRM_COPY.previewFailedLine
        : CRM_GRID_COPY.match.empty,
    onLeadDetailClose: onLeadDetailClosed,
  };

  // ── Send lens ────────────────────────────────────────────────────────────
  function outcomeCell(l: BatchLead, r: CommitResultRow) {
    const name = l.preview || NO_NAME;
    return (
      <Cell>
        <Space size={space.xs} align="center" wrap data-testid={`crm-grid-result-${l.id}`}>
          <Tag color={OUTCOME_TAG_COLOR[r.outcome]} style={{ margin: 0 }}>
            {r.outcome === 'incomplete' && r.heldFields && r.heldFields.length > 0
              ? CRM_COPY.incompleteHeldLabel
              : CRM_COPY.outcomeLabel[r.outcome]}
          </Tag>
          {r.outcome === 'changed_since_preview' && (
            <>
              <Button size="small" onClick={() => onCommitted()} aria-label={`${CRM_COPY.recheckButton} ${name}`}>
                {CRM_COPY.recheckButton}
              </Button>
              <HelpHint {...CRM_COPY.help.sendRecheck} testId={`crm-help-send-recheck-${l.id}`} />
            </>
          )}
        </Space>
        {r.reason && (
          <Caption type={r.outcome === 'failed' ? 'danger' : r.outcome === 'incomplete' ? 'warning' : 'secondary'}>
            {r.reason}
          </Caption>
        )}
        {r.activities && r.activities.logged > 0 && <Caption>{CRM_COPY.activity.logged(r.activities.logged)}</Caption>}
        {r.activities && r.activities.failed > 0 && (
          <Caption type="warning">{CRM_COPY.activity.failed(r.activities.failed)}</Caption>
        )}
        {r.eventLink && CRM_COPY.eventLinkNote[r.eventLink] && <Caption>{CRM_COPY.eventLinkNote[r.eventLink]}</Caption>}
        {r.taskOwner === 'integration_account' && <Caption type="warning">{CRM_COPY.taskOwnerFallback}</Caption>}
      </Cell>
    );
  }

  const notReadyCount = previews.filter((p) => !isReady(p)).length;
  // Already sent, nothing new since: the 'changed' read leaves them out, so
  // they have no preview row. Send lists them anyway, off the lead rows, so
  // what already went to the CRM stays in sight once the results are
  // cleared, after a reload, and on any later visit. Never tickable.
  const isAlreadySent = (l: BatchLead) => kindOf(l.id).kind === 'sent_unchanged';
  const alreadySentCount = lensRows.leads.filter(isAlreadySent).length;
  const resultParts = useMemo(() => {
    if (!outcome) return [];
    const byOutcome = new Map<string, number>();
    for (const r of outcome.results) {
      const label =
        r.outcome === 'incomplete' && r.heldFields && r.heldFields.length > 0
          ? CRM_COPY.incompleteHeldLabel
          : CRM_COPY.outcomeLabel[r.outcome];
      byOutcome.set(label, (byOutcome.get(label) ?? 0) + 1);
    }
    return [...byOutcome].map(([label, n]) => `${n} ${label}`);
  }, [outcome]);

  const sendToolbar = (
    <Space direction="vertical" size={space.xs} style={{ width: '100%' }}>
      {staleLine}
      {outcome && (
        <Space size={space.xs} align="center" wrap data-testid="crm-grid-results">
          <Typography.Text strong>{`${CRM_COPY.resultsHeader} (${outcome.results.length})`}</Typography.Text>
          {resultParts.length > 0 && (
            <Typography.Text type="secondary">{CRM_GRID_COPY.send.resultsLine(resultParts)}</Typography.Text>
          )}
          <HelpPopover {...CRM_COPY.help.sendResults} testId="crm-help-send-results" />
          <Button size="small" onClick={() => setOutcome(null)}>
            {CRM_GRID_COPY.send.clearResults}
          </Button>
        </Space>
      )}
      <Space size={space.md} align="center" wrap>
        <Space size={space.xs} align="center">
          <Typography.Text strong data-testid="crm-grid-ready-count">
            {previewLoaded
              ? `${CRM_COPY.readySectionHeader} (${selectedIds.length}/${readyIds.size})`
              : CRM_COPY.readySectionHeader}
          </Typography.Text>
          <HelpHint {...CRM_COPY.help.sendReadyGroup} testId="crm-help-send-ready" />
        </Space>
        {notReadyCount > 0 && (
          <Space size={space.xs} align="center">
            <Typography.Text type="secondary">{`${CRM_COPY.notReadySectionHeader} (${notReadyCount})`}</Typography.Text>
            <HelpPopover {...CRM_COPY.help.sendNotReady} testId="crm-help-send-not-ready" />
          </Space>
        )}
        {alreadySentCount > 0 && (
          <Space size={space.xs} align="center" data-testid="crm-grid-already-sent">
            <Typography.Text type="secondary">{`${CRM_GRID_COPY.send.alreadySentHeader} (${alreadySentCount})`}</Typography.Text>
            <HelpHint {...CRM_GRID_COPY.help.alreadySent} testId="crm-help-send-already-sent" />
            {/* The delta disclosure's one-way re-request under scope 'all'. */}
            <Button type="link" size="small" onClick={() => dispatch({ type: 'showAllPreview' })}>
              {CRM_GRID_COPY.send.includeAlreadySent}
            </Button>
          </Space>
        )}
      </Space>
      <Typography.Text type="secondary" data-testid="crm-send-policy-line">
        {`${CRM_COPY.policyOnSendPrefix} ${
          sendPolicy === 'overwrite' ? CRM_COPY.policyOverwrite : CRM_COPY.policyFillBlanks
        }. ${CRM_COPY.policyOnSendSuffix}`}
      </Typography.Text>
      {summaryText && (
        <Space size={space.xs} align="center">
          <Typography.Text data-testid="crm-grid-send-summary">{summaryText}</Typography.Text>
          <HelpHint {...CRM_COPY.help.sendSummary} testId="crm-help-send-summary" />
        </Space>
      )}
      {ownersUnavailable && (
        <Alert
          type="warning"
          showIcon
          message={CRM_COPY.sendDisclosures.ownersUnavailable}
          data-testid="crm-send-disclosure-owners-unavailable"
        />
      )}
      {needsEmailCount > 0 && (
        <Typography.Text type="secondary" data-testid="crm-send-disclosure-needs-email">
          {CRM_COPY.sendDisclosures.needsEmail(needsEmailCount)}
        </Typography.Text>
      )}
      {integrationAccountOwnedCount > 0 && (
        <Typography.Text type="secondary" data-testid="crm-send-disclosure-integration-account">
          {CRM_COPY.sendDisclosures.integrationAccountOwned(integrationAccountOwnedCount)}
        </Typography.Text>
      )}
      {ownerUnverifiedCount > 0 && (
        <Typography.Text type="secondary" data-testid="crm-send-disclosure-owner-unverified">
          {CRM_COPY.sendDisclosures.ownerUnverified(ownerUnverifiedCount)}
        </Typography.Text>
      )}
      {/* The already-sent leads are listed above, with the way to include
          them; the backend's count only speaks when none of them is a row. */}
      {alreadySentCount === 0 && collapsedCount > 0 && <CollapsedLine count={collapsedCount} dispatch={dispatch} />}
      <Space size={space.sm} align="center" wrap>
        {/* Without the footer slot the button stays with the facts it commits to. */}
        {!host.actionSlot && sendButton}
        {sendDisabledReason && (
          <Typography.Text type="secondary" data-testid="crm-send-disabled-reason">
            {sendDisabledReason}
          </Typography.Text>
        )}
        {canManage && host.rows.failed && (
          <Button
            size="small"
            aria-label={CRM_GRID_COPY.send.retryRowsLabel}
            onClick={host.rows.reload}
          >
            {CRM_COPY.retryButton}
          </Button>
        )}
      </Space>
    </Space>
  );

  const sendLens: GridLens = {
    key: 'send',
    rows: lensRows,
    // Who this send could include, anyone it just went to (so their outcome
    // stays on screen once the re-read collapses them), and everyone already
    // sent with nothing new, so what is in the CRM can be told from what
    // isn't yet.
    rowFilter: (l) => previewById.has(l.id) || outcomeById.has(l.id) || isAlreadySent(l),
    statusColumn: {
      title: statusTitle,
      width: STATUS_WIDTH,
      render: (l) => {
        const r = outcomeById.get(l.id);
        if (r) return outcomeCell(l, r);
        const p = previewById.get(l.id);
        const info = kindOf(l.id);
        return (
          <Cell>
            {pillRow(l, info)}
            {p && info.kind === 'held' && <Caption type="warning">{unreadyReason(p)}</Caption>}
            {/* The rules' "Incomplete" on a lead that still goes (flag mode). */}
            {p && isReady(p) && p.completeness?.complete === false && (
              <Tooltip title={failureSummary(p.completeness.failures)}>
                <Tag color="gold" style={{ margin: 0 }}>
                  {LEAD_RULES_COPY.incompleteTag}
                </Tag>
              </Tooltip>
            )}
            {p && ownerFlag(p)}
            {sentCaption(l)}
          </Cell>
        );
      },
      sortValue: statusSort,
      filterValue: (l) => {
        const r = outcomeById.get(l.id);
        return r ? CRM_COPY.outcomeLabel[r.outcome] : statusFilterValue(l);
      },
    },
    selection: {
      selectedIds,
      onChange: (ids) => {
        // Moves only the people the operator just ticked or unticked, and only
        // among the ready ones — a deselection made elsewhere stays as set.
        const next = new Set(ids);
        const current = new Set(selectedIds);
        const added = ids.filter((id) => !current.has(id) && readyIds.has(id));
        const removed = selectedIds.filter((id) => !next.has(id));
        if (added.length > 0) dispatch({ type: 'setAllSelected', personIds: added, selected: true });
        if (removed.length > 0) dispatch({ type: 'setAllSelected', personIds: removed, selected: false });
      },
      disabledReason: (l) => {
        if (!canManage) return CRM_GRID_COPY.send.readOnlyReason;
        const p = previewById.get(l.id);
        if (!p) {
          if (!previewLoaded) return CRM_GRID_COPY.send.checkingReason;
          return isAlreadySent(l) ? CRM_GRID_COPY.send.alreadySentReason : CRM_GRID_COPY.send.notInPreview;
        }
        return isReady(p) ? null : unreadyReason(p);
      },
      ariaLabel: (l) => `Include ${l.preview || NO_NAME}`,
    },
    toolbar: sendToolbar,
    emptyText: previewLoading
      ? CRM_COPY.previewLoadingLine
      : previewFailed
        ? CRM_COPY.previewFailedLine
        : CRM_GRID_COPY.match.empty,
    onLeadDetailClose: onLeadDetailClosed,
  };

  const lens = view === 'clean' ? cleanLens : view === 'match' ? matchLens : sendLens;

  const hint =
    view === 'clean'
      ? { text: CRM_GRID_COPY.help.tabClean.text, testId: 'crm-hint-tab-clean' }
      : view === 'match'
        ? { text: CRM_COPY.help.tabMatch.text, testId: 'crm-hint-tab-match' }
        : { text: CRM_COPY.help.tabSend.text, testId: 'crm-hint-tab-send' };

  const detailsPerson = view === 'match' && detailsId ? (previewById.get(detailsId) ?? null) : null;

  return (
    <>
      {view !== 'leads' && !hidden && (
        <div data-testid={`crm-grid-${view}`}>
          <div style={{ padding: `0 ${space.lg}px` }}>
            <HintLine text={hint.text} testId={hint.testId} />
          </div>
          {host.renderGrid(lens)}
        </div>
      )}
      {view === 'send' && host.actionSlot && createPortal(sendButton, host.actionSlot)}
      <MatchDetailsDrawer
        person={detailsPerson}
        choices={detailsPerson ? state.fieldChoices[detailsPerson.personId] : undefined}
        canManage={canManage}
        effectivePolicy={effectivePolicy}
        overwriteLocked={overwriteLocked}
        onPick={(field, choice) => {
          if (detailsPerson) dispatch({ type: 'pickField', personId: detailsPerson.personId, field, choice });
        }}
        onClose={() => setDetailsId(null)}
        onEditOurs={
          detailsPerson
            ? async (field, value) => {
                // Re-reads both queries and the rows.
                await onEditLead(detailsPerson.personId, { [field]: value });
              }
            : undefined
        }
      />
    </>
  );
}

/**
 * Clean's toolbar: legacy Clean's "Needs email (N)" header (the match check's
 * count) and, beside it, the rule-held count off the lead rows (D9).
 */
function CleanToolbar({ noEmail, held }: { noEmail: number; held: number | null }) {
  return (
    <Space size={space.lg} wrap align="center">
      <Space size={space.xs} align="center">
        <Typography.Text strong data-testid="crm-grid-clean-needs-email">
          {`${CRM_COPY.needsEmailHeader} (${noEmail})`}
        </Typography.Text>
        <HelpHint {...CRM_COPY.help.cleanQueue} testId="crm-help-clean-queue" />
      </Space>
      <Space size={space.xs} align="center">
        <Typography.Text strong data-testid="crm-grid-clean-held">
          {held === null ? CRM_GRID_COPY.clean.heldHeader : `${CRM_GRID_COPY.clean.heldHeader} (${held})`}
        </Typography.Text>
        <HelpHint {...CRM_GRID_COPY.help.held} testId="crm-help-clean-held" />
      </Space>
    </Space>
  );
}

/**
 * The delta disclosure (spec 2026-08-30 send-what-changed §5a), today's copy
 * and today's one-way "Show them" re-request under `scope: 'all'`.
 */
function CollapsedLine({ count, dispatch }: { count: number; dispatch: Dispatch<CrmQueueAction> }) {
  return (
    <div data-testid="crm-match-collapsed-line">
      <Space size={space.xs} align="center" wrap>
        <Typography.Text type="secondary">{CRM_COPY.collapsedLine(count)}</Typography.Text>
        <Button type="link" size="small" onClick={() => dispatch({ type: 'showAllPreview' })}>
          {CRM_COPY.showAllLink}
        </Button>
      </Space>
    </div>
  );
}
